import { decryptMessage, encodeBase64 } from './crypto.js';
import { epochKeyRing } from './epoch-key-ring.js';
/**
 * Decrypt a channel-encrypted message, picking the correct symmetric key
 * based on the message's `keyEpoch`.
 *
 * Selection rules (first match wins):
 *   1. If `keyEpoch` is a number and the ring has a key for
 *      `(channelId, keyEpoch)`, decrypt with that epoch's key.
 *   2. Otherwise (legacy rows with `keyEpoch === null | undefined`, or
 *      an epoch-aware row whose key is not cached) fall back to the
 *      `legacyChannelKey` passed in by the caller. This is typically
 *      whatever `useCryptoStore.channelKeys[channelId]` holds — i.e.
 *      the pre-epoch channel key that was already in the client's
 *      in-memory store before epoch rotation shipped.
 *   3. If neither is available, returns `null` — callers should render
 *      the standard "encryption key unavailable" placeholder.
 *
 * This helper is the single decryption entry point shared by web,
 * mobile, and extension so the fallback policy stays consistent.
 *
 * Pure — the ring is a module-scoped singleton and `legacyChannelKey`
 * is supplied by the caller, so there's no platform-specific I/O here.
 */
export function decryptChannelMessage(encryptedContent, channelId, keyEpoch, legacyChannelKey) {
    // 1. Epoch-aware path — look up the exact epoch's key in the ring.
    if (typeof keyEpoch === 'number' && Number.isFinite(keyEpoch) && keyEpoch >= 0) {
        const epochKeyBytes = epochKeyRing.get(channelId, keyEpoch);
        if (epochKeyBytes) {
            return decryptMessage(encryptedContent, encodeBase64(epochKeyBytes));
        }
        // Ring miss — try the legacy key. A historical message may still
        // be decryptable with the legacy flat key if the client was online
        // before rotation and cached the key through the pre-epoch path.
    }
    // 2. Legacy fallback.
    if (legacyChannelKey) {
        return decryptMessage(encryptedContent, legacyChannelKey);
    }
    // 3. No key available at all.
    return null;
}
//# sourceMappingURL=epoch-decrypt.js.map